import { useEffect, useId, useRef, type ReactNode } from 'react';

interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
  wide?: boolean;
}

/** Shared modal shell for Integrations dialogs (Stage 19I) — matches global search dialog chrome. */
export function Modal({ title, onClose, children, testId, wide }: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const focusable = panelRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
  }, []);

  return (
    <div className="ig-modal-overlay" data-testid={testId ?? 'ig-modal'}>
      <button
        type="button"
        className="ig-modal-overlay__backdrop"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className={`ig-modal${wide ? ' ig-modal--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="ig-modal__header">
          <h2 id={titleId} className="ig-modal__title">
            {title}
          </h2>
          <button type="button" className="btn ghost compact" onClick={onClose} data-testid="ig-modal-close">
            Close
          </button>
        </div>
        <div className="ig-modal__body">{children}</div>
      </div>
    </div>
  );
}
